import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Select } from 'antd';

const { Option } = Select;

export default function SignupPage() {
  const { supabase, setUser, setUserProfile } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [teamIds, setTeamIds] = useState([]);
  const [role, setRole] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchTeams() {
      setTeamsLoading(true);
      setTeamsError('');
      try {
        // Try to get auth token if user is already logged in
        const { data: { session } } = await supabase.auth.getSession();
        const headers = session?.access_token ? {
          'Authorization': `Bearer ${session.access_token}`
        } : {};
        
        // Fetch teams with role-based filtering
        const teamsResponse = await fetch('/api/teams/filtered', { headers });
        
        // If 401 (not authenticated), fallback to direct query
        // This is expected on signup page where user isn't logged in yet
        if (teamsResponse.status === 401) {
          const { data, error } = await supabase.from('teams').select('id, name');
          if (error) throw error;
          setTeams(data || []);
          return;
        }
        
        const teamsResult = await teamsResponse.json();
        
        if (teamsResult.success) {
          setTeams(teamsResult.data || []);
        } else {
          throw new Error(teamsResult.error || 'Failed to fetch teams');
        }
      } catch (err) {
        // Fallback to direct database query
        try {
          const { data, error } = await supabase.from('teams').select('id, name');
          if (error) throw error;
          setTeams(data || []);
        } catch (fallbackErr) {
          setTeamsError('Failed to load teams');
        }
      } finally {
        setTeamsLoading(false);
      }
    }
    fetchTeams();
  }, [supabase]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.match(/^[^@]+@[^@]+\.[^@]+$/)) {
      setError('Invalid email format');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError("Password didn't match");
      return;
    }
    if (!teamIds || teamIds.length === 0) {
      setError('Please select at least one team');
      return;
    }
    // Check if "No Team" (-1) is selected with other teams
    if (teamIds.includes(-1) && teamIds.length > 1) {
      setError('Cannot select "No Team" with other teams');
      return;
    }
    if (!role) {
      setError('Please select a role');
      return;
    }
    setLoading(true);
    // 1. Create Supabase Auth user
    const { data, error: signupError } = await supabase.auth.signUp({ email, password });
    const user = data?.user;
    if (signupError) {
      setError(signupError.message);
      setLoading(false);
      return;
    }
    if (!user) {
      setError('Signup failed.');
      setLoading(false);
      return;
    }
    setUser(user);
    
    // 2. Insert into users table with team_ids
    const insertData = { 
      id: user.id, 
      email, 
      full_name: fullName,
      role: role,
      // Always store team_ids, even if it's [-1] for "No Team"
      team_ids: teamIds.map(id => parseInt(id, 10))
    };
    
    const { data: userData, error: userError } = await supabase
      .from('users')
      .insert(insertData)
      .single();
    if (userError) {
      setError(userError.message);
      setLoading(false);
      return;
    }
    setUserProfile(userData);
    setSignupSuccess(true);
    setLoading(false);
  };

  if (signupSuccess) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001', padding: 40, width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 20, textAlign: 'center' }}>
          <img 
            src="https://amicredit.ro/wp-content/uploads/2024/02/amicredit.svg" 
            alt="AMI Credit" 
            style={{ width: 180, margin: '0 auto 12px auto' }} 
            onError={(e) => {
              // Fallback if logo image doesn't exist
              e.target.style.display = 'none';
              // Show fallback text
              const fallback = document.createElement('div');
              fallback.innerHTML = '<span style="color: #00d084; font-weight: 700; font-size: 24px;">Ami</span><span style="color: #374151; font-weight: 700; font-size: 24px;">Credit</span>';
              fallback.style.textAlign = 'center';
              fallback.style.margin = '0 auto 12px auto';
              e.target.parentNode.insertBefore(fallback, e.target);
            }}
          />
          <div style={{ marginBottom: 8 }}>
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ margin: '0 auto' }}
            >
              <circle cx="12" cy="12" r="10" fill="#00d084"/>
              <path d="m9 12 2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 style={{ margin: 0, fontWeight: 700, color: '#00d084' }}>Account Created Successfully!</h2>
          <p style={{ margin: 0, color: '#555', lineHeight: 1.5 }}>
            Email verification was sent to <strong>{email}</strong> for account verification. 
            Please check your inbox and click the verification link to activate your account.
          </p>
          <p style={{ margin: 0, color: '#888', fontSize: 14 }}>
            You'll be able to login once your email is verified.
          </p>
          <Link 
            to="/login" 
            style={{ 
              background: '#e65c00', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '12px 0', 
              fontSize: 17, 
              fontWeight: 600, 
              textDecoration: 'none',
              display: 'block',
              marginTop: 12
            }}
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
      <form onSubmit={handleSignup} style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001', padding: 40, width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <img 
          src="https://amicredit.ro/wp-content/uploads/2024/02/amicredit.svg" 
          alt="AMI Credit" 
          style={{ width: 180, margin: '0 auto 12px auto' }} 
          onError={(e) => {
            // Fallback if logo image doesn't exist
            e.target.style.display = 'none';
            // Show fallback text
            const fallback = document.createElement('div');
            fallback.innerHTML = '<span style="color: #00d084; font-weight: 700; font-size: 24px;">Ami</span><span style="color: #374151; font-weight: 700; font-size: 24px;">Credit</span>';
            fallback.style.textAlign = 'center';
            fallback.style.margin = '0 auto 12px auto';
            e.target.parentNode.insertBefore(fallback, e.target);
          }}
        />
        <h2 style={{ margin: 0, fontWeight: 700 }}>Create your account</h2>
        <p style={{ margin: 0, color: '#555' }}>Sign up to get started with your new account.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="e.g. John Doe"
            style={{ padding: 10, borderRadius: 6, border: '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="e.g. mycompany@email.com"
            style={{ padding: 10, borderRadius: 6, border: error && !email.match(/^[^@]+@[^@]+\.[^@]+$/) ? '1.5px solid #e74c3c' : '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          />
          {error && !email.match(/^[^@]+@[^@]+\.[^@]+$/) && <span style={{ color: '#e74c3c', fontSize: 13 }}>Invalid email format</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Create a strong password"
            style={{ padding: 10, borderRadius: 6, border: error && password.length < 6 ? '1.5px solid #e74c3c' : '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            style={{ padding: 10, borderRadius: 6, border: error && password !== confirmPassword ? '1.5px solid #e74c3c' : '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          />
          {error && password !== confirmPassword && <span style={{ color: '#e74c3c', fontSize: 13 }}>Password didn&apos;t match</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Select Team(s)</label>
          {teamsLoading ? (
            <div style={{ color: '#888', fontSize: 14 }}>Loading teams...</div>
          ) : teamsError ? (
            <div style={{ color: '#e74c3c', fontSize: 14 }}>{teamsError}</div>
          ) : (
            <Select
              mode="multiple"
              value={teamIds}
              onChange={(value) => {
                // Validate: cannot select -1 (No Team) with other teams
                if (Array.isArray(value)) {
                  if (value.includes(-1) && value.length > 1) {
                    // If -1 is selected with others, keep only -1
                    setTeamIds([-1]);
                  } else {
                    setTeamIds(value);
                  }
                } else {
                  setTeamIds(value ? [value] : []);
                }
              }}
              placeholder="Select team(s)"
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value={-1}>No Team</Option>
              {teams.map(team => (
                <Option key={team.id} value={team.id}>{team.name}</Option>
              ))}
            </Select>
          )}
          <div style={{ fontSize: 12, color: '#888' }}>
            You can select multiple teams
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>Select Role</label>
          <select
            value={role}
            onChange={e => setRole(e.target.value)}
            style={{ padding: 10, borderRadius: 6, border: '1.5px solid #eee', outline: 'none', fontSize: 16 }}
            required
          >
            <option value="">Select a role</option>
            <option value="admin">Admin</option>
            <option value="team_lead">Team Lead</option>
          </select>
        </div>
        {error && <div style={{ color: '#e74c3c', fontSize: 15, marginTop: -8 }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ background: '#e65c00', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 0', fontSize: 17, fontWeight: 600, cursor: 'pointer', marginBottom: 8, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Creating account...' : 'Create Account'}
        </button>
        <div style={{ textAlign: 'center', color: '#555', fontSize: 15 }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#222', fontWeight: 600 }}>Login</Link>
        </div>
      </form>
    </div>
  );
} 